import {
  memo, useState, useEffect, useMemo,
} from 'react';
import { CircularProgress } from '@mui/material';
import { useListeners, useSelector } from '@client/hooks/redux.hooks';
import { getSessionUserAdminStatus, getUserId, getSessionUserState } from '@client/selectors/helpers/login';
import { hasFirebaseInstance, hasFirebaseAuth, hasFirebaseScaffolded } from '@client/selectors/helpers/clients';
import type { ActionCreatorWithPayload } from '@reduxjs/toolkit/dist/createAction';
import type { History, Location } from 'history';
import type { match as Match } from 'react-router';

export interface WithDataOptions {
  readonly useVanityUrl?: boolean;
  readonly enforceAuth?: boolean;
  readonly hasLoaderDefault?: boolean;
  readonly loaderTimeout?: number;
  readonly fallback?: React.ElementType;
}

interface WithDataProps {
  readonly canAccess: boolean;
  readonly current: string;
  readonly fetch?: ((id?: string, useVanityUrl?: boolean) => void) | ActionCreatorWithPayload<any>;
  readonly unfetch?: ((id?: string) => void) | ActionCreatorWithPayload<any>;
  readonly history?: History;
  readonly staticContext?: any;
  readonly location?: Location<unknown>;
  readonly match?: Match;
}

export const withData = <C extends React.ElementType>(
  WrappedComponent: C, options: WithDataOptions = {}, listeners = null,
) => memo<WithDataProps>(({
    canAccess, current, fetch, unfetch,
    history, staticContext, location, match,
    ...restProps
  }) => {
    const isAdmin = useSelector(getSessionUserAdminStatus);
    const userId = useSelector(getUserId);
    const hasFirebase = useSelector(hasFirebaseInstance);
    const userState = useSelector(getSessionUserState);
    const hasAuth = useSelector(hasFirebaseAuth);
    const hasScaffolded = useSelector(hasFirebaseScaffolded);

    const arrayListeners = Array.isArray(listeners) ? listeners : [listeners];
    useListeners(...arrayListeners);

    const {
      useVanityUrl = false, enforceAuth = false, hasLoaderDefault = false,
      loaderTimeout = 1000,
    } = options;

    const [hasLoader, setHasLoader] = useState(hasLoaderDefault);

    const isActive = useMemo(
      () => fetch
    && hasFirebase
    && userState !== null
    && (userState === false || (userState === true && hasScaffolded))
    && (!enforceAuth || (enforceAuth && hasAuth)),
      [enforceAuth, fetch, hasAuth, hasFirebase, userState, hasScaffolded],
    );

    useEffect(() => {
      if (isActive) {
        const timeout = setTimeout(() => setHasLoader(true), loaderTimeout);

        fetch(current, useVanityUrl);

        return () => {
          clearTimeout(timeout);

          if (unfetch) {
            unfetch(current);
          }
        };
      }

      return undefined;
    }, [isActive, current, fetch, unfetch, useVanityUrl, userId, loaderTimeout]);

    if (!canAccess) {
      const { fallback } = options;

      if (hasLoader && fallback) {
        const FallbackComponent = fallback as any;

        return <FallbackComponent />;
      }

      if (fallback === null) {
        return null;
      }

      if (hasLoader) {
        return (
          <div className="spacer">
            <CircularProgress />
          </div>
        );
      }

      return <div className="spacer" />;
    }

    let matchProps = {};

    if (match && match.params) {
      matchProps = match.params;
    }

    const Component = WrappedComponent as any;

    return (
      <Component
        isAdmin={isAdmin}
        current={current}
        {...restProps}
        {...matchProps}
      />
    );
  });
